<template>
  <div id="app-area">
    <Header v-if="isLoggedIn && isProfileUpToDate && hasRoute()" :activeLayout="activeLayout" />

    <div class="app-area-container" :id="bodyAreaId" :class="{ loggedout: !isLoggedIn && hasRoute() }">
      <Links v-if="isLoggedIn && isProfileUpToDate && hasRoute()" @isOpen="(val) => (sideNavisOpen = val)" :activeLayout="activeLayout" />

      <div :sideNavisOpen="!isFullScreen && sideNavisOpen && isLoggedIn && isProfileUpToDate && hasRoute() ? true : null" :id="contentAreaId">
        <Target />
      </div>
    </div>

    <Footer :activeLayout="activeLayout || ''" />
  </div>
</template>

<script setup>
// Layout B: every page outside /auth/ (router.js); the login's screens run in AuthLayout
import { ref, computed, onMounted, nextTick, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { store } from '@/legacyStore'
import { registerUserActivity } from '@/vuex-actions'
import Target from '@/components/Target'
import Links from '@/components/Links'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import { bus } from '@/main.js'

const sideNavisOpen = ref(true)
const isFullScreen = ref(false)
const route = useRoute()
// before the first route resolves it has no name: no header, no menu yet
const hasRoute = () => route.name != null
const activeLayout = store.getters.activeLayout
const isLoggedIn = store.getters.isLoggedIn
const isProfileUpToDate = store.state.clientSession.isProfileUpToDate

const bodyAreaId = computed(() => {
  if (isLoggedIn && isProfileUpToDate && hasRoute()) {
    return isFullScreen.value ? 'app-body-area-cp' : 'app-body-area'
  }
  return 'app-body-area-cp'
})

const contentAreaId = computed(() => {
  if (!isProfileUpToDate) {
    return 'content-area-full'
  }

  if ((activeLayout === 'dev' || activeLayout === 'administrative') && hasRoute()) {
    return isFullScreen.value ? 'content-area-full' : 'content-area'
  }

  return 'content-area-full'
})

const resTimeout = ref(null)
const handleRes = () => {
  clearTimeout(resTimeout.value)
  resTimeout.value = setTimeout(() => {
    location.reload()
  }, 500)
}

const handleAppAreaResize = () => {
  nextTick(() => {
    const appArea = document.querySelector('#app-area')
    const appAreaContainer = document.querySelector('.app-area-container')

    if (appAreaContainer) {
      const sideMenuContainer = document.getElementById('sidemenu-container')
      const sideMenuWidth = sideMenuContainer ? sideMenuContainer.clientWidth : 350
      const windowWidth = window.innerWidth

      if (sideNavisOpen.value && sideMenuContainer && sideMenuWidth) {
        appAreaContainer.style.width = windowWidth - sideMenuWidth + 'px'
      } else {
        appAreaContainer.style.width = '100%'
      }
    }

    if (appArea) {
      appArea.style.opacity = '1'
    }
  })
}

// Activity tracking
const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
const activityThrottle = ref(null)
const THROTTLE_DELAY = 30000 // 30 seconds

const handleUserActivity = () => {
  // Throttle activity registration to avoid excessive calls
  if (!activityThrottle.value) {
    store.commit(registerUserActivity)

    activityThrottle.value = setTimeout(() => {
      activityThrottle.value = null
    }, THROTTLE_DELAY)
  }
}

const registerActivityListeners = () => {
  activityEvents.forEach((eventType) => {
    document.addEventListener(eventType, handleUserActivity, { passive: true })
  })
}

const removeActivityListeners = () => {
  activityEvents.forEach((eventType) => {
    document.removeEventListener(eventType, handleUserActivity)
  })
}

// a named handler, so the unmount can remove it: this layout unmounts on the way to /auth/ and mounts again after a login
const onFullScreenToggle = (val) => {
  isFullScreen.value = val
}

onMounted(() => {
  bus.on('full-screen-toggle', onFullScreenToggle)

  setTimeout(() => {
    handleAppAreaResize()
  }, 1500)

  // Register activity listeners only if user is logged in
  if (isLoggedIn) {
    registerActivityListeners()
  }
})

onBeforeUnmount(() => {
  bus.off('full-screen-toggle', onFullScreenToggle)
  window.removeEventListener('resize', handleRes)
  removeActivityListeners()

  // Clear throttle timeout
  if (activityThrottle.value) {
    clearTimeout(activityThrottle.value)
  }
})
</script>
